"use client";

import { Suspense, useEffect, useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { useRouter, useSearchParams } from "next/navigation";
import { FiDollarSign, FiPieChart, FiUsers, FiClock, FiTarget } from "react-icons/fi";
import { useSolanaUSDCBalance } from "@/hooks/useSolanaUSDCBalance";
import { useBnbBalance } from "@/hooks/useBnbBalance";
import { isClosedDust } from "./dust";
import PortfolioProfileBar from "./components/PortfolioProfileBar";
import WalletCardsSection from "./components/WalletCardsSection";
import PnLChart from "./components/PnLChart";
import CategoryBreakdown from "./components/CategoryBreakdown";
import PositionsSection from "./components/PositionsSection";
import CopyTradingSection from "./components/CopyTradingSection";
import RecentActivitySection from "./components/RecentActivitySection";
import RedeemablePositionsCard from "./components/RedeemablePositionsCard";
import PolymarketRewardsCard from "./components/PolymarketRewardsCard";
import PriceAlertsSection from "./components/PriceAlertsSection";
import LoginPromptModal from "@/app/components/LoginPromptModal";
import { useUnifiedPositions } from "./hooks/useUnifiedPositions";
import { usePortfolioSocket } from "@/hooks/usePortfolioSocket";
import { useT } from "@/hooks/useT";

type TabType = "positions" | "activity" | "copytrading" | "alerts";

// Wrapper component to handle Suspense for useSearchParams
function PortfolioContent() {
  const t = useT();
  const { isLoggedIn, isReady } = useAuth();
  const router = useRouter();
  const searchParams = useSearchParams();
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  // Solana balance for the breakdown
  const { balance: solanaBalance, refetch: refetchSolana } = useSolanaUSDCBalance();

  // BNB/USDT balance for Predict.fun — kept wired even though the slice
  // is hidden from the hero/balance bar right now; we'll re-enable Predict
  // soon and don't want to rebuild the plumbing.
  const { usdtBalance: bnbUsdtBalance } = useBnbBalance();

  // Unified positions (PM + Kalshi + Predict + HL)
  const {
    positions: unifiedPositions,
    pmSummary,
    kalshiSummary,
    predictSummary,
    hyperliquidSummary,
    loading: positionsLoading,
    error: positionsError,
    refetch: refetchPositions,
  } = useUnifiedPositions("all");

  // Aggregate open/closed counts from the unified positions list so the
  // profile bar agrees with the per-wallet cards below. Without this,
  // backend `stats.openPositions` was 2 while the wallet cards summed
  // 0+0+0 — Predict positions exist but have no wallet card, so they
  // were "ghost open positions" the user couldn't reconcile.
  const unifiedOpenCount =
    pmSummary.count + kalshiSummary.count + predictSummary.count + hyperliquidSummary.count;
  // Exclude structural dust so the "N closed" stat matches the Closed list
  // (which hides the same rounding leftovers). Real sold/won/lost rows count.
  const unifiedClosedCount = unifiedPositions.filter((p) => !p.isOpen && !isClosedDust(p)).length;

  // Fetch Solana balance on mount
  useEffect(() => {
    if (isLoggedIn) refetchSolana();
  }, [isLoggedIn]);

  // Get tab from URL or default to positions — use local state for immediate updates.
  // Dev-only convenience (2026-05-26): `?mockActivity=1` and `?mockPositions=1`
  // are localhost preview toggles consumed by the section components. When set
  // they auto-select the tab they preview so the URL alone is enough to land
  // on the right view (`/portfolio?mockActivity=1` lands on Activity instead
  // of bouncing through Positions first).
  const tabFromUrl = searchParams.get("tab") as TabType | null;
  const validTabs: TabType[] = ["positions", "activity", "copytrading", "alerts"];
  const isDevMockActivity = process.env.NODE_ENV === 'development' && searchParams.get("mockActivity") === "1";
  const isDevMockCopytrading = process.env.NODE_ENV === 'development' && searchParams.get("mockCopytrading") === "1";
  const initialTab: TabType =
    tabFromUrl && validTabs.includes(tabFromUrl)
      ? tabFromUrl
      : isDevMockCopytrading
        ? "copytrading"
        : isDevMockActivity
          ? "activity"
          : "positions";
  const [activeTab, setActiveTabState] = useState<TabType>(initialTab);

  // Sync local state with URL changes (e.g., browser back/forward).
  // Honors the same mock fallbacks as the initial-tab logic so a hard
  // refresh of /portfolio?mockActivity=1 (or ?mockCopytrading=1) doesn't
  // bounce off the previewed tab back to Positions in the first effect tick.
  useEffect(() => {
    const urlTab = tabFromUrl && validTabs.includes(tabFromUrl)
      ? tabFromUrl
      : isDevMockCopytrading
        ? "copytrading"
        : (isDevMockActivity ? "activity" : "positions");
    if (urlTab !== activeTab) {
      setActiveTabState(urlTab);
    }
  }, [tabFromUrl, isDevMockActivity, isDevMockCopytrading]);

  // Update URL when tab changes
  const setActiveTab = (tab: TabType) => {
    setActiveTabState(tab); // Immediate UI update
    try {
      const params = new URLSearchParams();
      if (tab !== "positions") {
        params.set("tab", tab);
      }
      const newUrl = params.toString() ? `/portfolio?${params.toString()}` : "/portfolio";
      router.replace(newUrl, { scroll: false });
    } catch {
      // Fallback if router fails
    }
  };

  // Show login modal if not logged in
  useEffect(() => {
    if (isReady && !isLoggedIn) {
      setShowLoginModal(true);
    }
  }, [isReady, isLoggedIn]);

  const handleRefresh = () => {
    setRefreshKey(prev => prev + 1);
    refetchPositions();
    refetchSolana();
  };

  // Real-time WebSocket updates: auto-refresh on trade events
  usePortfolioSocket({
    onPositionBought: () => handleRefresh(),
    onPositionSold: () => handleRefresh(),
    onBalanceUpdate: () => refetchSolana(),
    onPortfolioRefresh: () => handleRefresh(),
    onTradeCopied: () => handleRefresh(),
  });

  if (!isReady) {
    return (
      <div className="w-full pt-6">
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="w-8 h-8 border-2 border-gray-400 dark:border-gray-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <>
      <LoginPromptModal
        isOpen={showLoginModal}
        onClose={() => {
          setShowLoginModal(false);
          router.push("/");
        }}
        message={t("portfolio.signInTitle")}
      />

      <div className="w-full pt-4 pb-24">

        {isLoggedIn ? (
          <>
            {/* 2026-05-26 (Pika): ProfileBar appears ABOVE the two-column
                layout on mobile (so it doesn't get dragged by the
                mobile order-flip of LEFT/RIGHT), and INSIDE the LEFT
                column on desktop (original behaviour — sits at the top
                of the sticky left rail). Two instances with show/hide
                visibility classes keeps the same component but the
                desktop layout unchanged. */}
            <div className="xl:hidden">
              <PortfolioProfileBar
                key={`profile-mobile-${refreshKey}`}
                onRefresh={handleRefresh}
                pmPositionsValue={pmSummary.totalValue}
                kalshiPositionsValue={kalshiSummary.totalValue}
                solanaBalance={solanaBalance}
                predictPositionsValue={predictSummary.totalValue}
                bnbUsdtBalance={bnbUsdtBalance}
                unifiedOpenCount={unifiedOpenCount}
                unifiedClosedCount={unifiedClosedCount}
              />
            </div>

            {/* Two-column layout on desktop: Left = stats, Right = tabs+content.
                On mobile, RIGHT (Tabs: Positions/Activity/CopyTrading/Alerts)
                appears BEFORE the LEFT block (Wallets / PnL / Redeemable /
                Rewards) via `order` — user feedback was that the wallets
                bock pushed the actionable tabs too far below the fold on
                phones. Desktop layout is unchanged. */}
            <div className="flex flex-col xl:flex-row gap-5 xl:mt-0 mt-4">

              {/* LEFT COLUMN — Wallets, Charts, Redeemable (sticky on desktop).
                  order-2 on mobile so it lands below the tabs; order-1 on
                  xl+ to keep it on the left side. */}
              <div className="order-2 xl:order-1 xl:w-[650px] xl:flex-shrink-0 space-y-4 xl:sticky xl:top-4 xl:self-start">
                {/* Desktop-only ProfileBar (mobile version above). Keeps the
                    original "profile sits at top of the LEFT sticky rail"
                    behaviour intact on xl+. */}
                <div className="hidden xl:block">
                  <PortfolioProfileBar
                    key={`profile-desktop-${refreshKey}`}
                    onRefresh={handleRefresh}
                    pmPositionsValue={pmSummary.totalValue}
                    kalshiPositionsValue={kalshiSummary.totalValue}
                    solanaBalance={solanaBalance}
                    predictPositionsValue={predictSummary.totalValue}
                    bnbUsdtBalance={bnbUsdtBalance}
                    unifiedOpenCount={unifiedOpenCount}
                    unifiedClosedCount={unifiedClosedCount}
                  />
                </div>

                {/* Per-wallet cards: deposit / Solana / legacy Safe / Privy login.
                    Lives between the profile bar (aggregate hero) and the PnL
                    chart so users see "where the money is" before "how it's
                    performed". */}
                <WalletCardsSection
                  key={`wallets-${refreshKey}`}
                  polymarketPositionsValue={pmSummary.totalValue}
                  polymarketPositionsCount={pmSummary.count}
                  kalshiPositionsValue={kalshiSummary.totalValue}
                  kalshiPositionsCount={kalshiSummary.count}
                  hyperliquidPositionsValue={hyperliquidSummary.totalValue}
                  hyperliquidPositionsCount={hyperliquidSummary.count}
                />

                {/* PnL Chart */}
                <PnLChart key={`chart-${refreshKey}`} />

                {/* Category Breakdown */}
                <CategoryBreakdown key={`categories-${refreshKey}`} />

                {/* Redeemable (both PM and Kalshi via useRedeemableQuery).
                 *  DFlowRedeemableCard was rendered too, duplicating the
                 *  Kalshi entry on screen + in the Claim modal totals. */}
                <RedeemablePositionsCard key={`redeemable-${refreshKey}`} />

                {/* Polymarket liquidity rewards + maker rebates */}
                <PolymarketRewardsCard key={`poly-rewards-${refreshKey}`} />
              </div>

              {/* RIGHT COLUMN — Tabs + Content (fills remaining space).
                  order-1 on mobile = appears ABOVE the wallets block
                  (Pika feedback). order-2 on xl+ keeps it on the right
                  side of the desktop two-column layout. */}
              <div className="order-1 xl:order-2 flex-1 min-w-0 space-y-4">
                {/* Tabs */}
                <div className="flex gap-1 p-1 bg-gray-100 dark:bg-[#18181D] rounded-[5px] border border-gray-200 dark:border-[#1A1A1A] transition-colors sticky top-0 z-10">
                  <button
                    onClick={() => setActiveTab("positions")}
                    className={`flex-1 flex items-center justify-center gap-1 py-2.5 rounded-[5px] font-satoshi font-medium text-xs sm:text-sm whitespace-nowrap transition-colors ${
                      activeTab === "positions"
                        ? "bg-white text-gray-900 shadow-sm"
                        : "text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                    }`}
                  >
                    <FiDollarSign className="w-3.5 h-3.5 sm:w-4 sm:h-4 shrink-0" />
                    {t("portfolio.tab.positions")}
                  </button>
                  <button
                    onClick={() => setActiveTab("activity")}
                    className={`flex-1 flex items-center justify-center gap-1 py-2.5 rounded-[5px] font-satoshi font-medium text-xs sm:text-sm whitespace-nowrap transition-colors ${
                      activeTab === "activity"
                        ? "bg-white text-gray-900 shadow-sm"
                        : "text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                    }`}
                  >
                    <FiClock className="w-3.5 h-3.5 sm:w-4 sm:h-4 shrink-0" />
                    {t("portfolio.tab.activity")}
                  </button>
                  <button
                    onClick={() => setActiveTab("copytrading")}
                    className={`flex-1 flex items-center justify-center gap-1 py-2.5 rounded-[5px] font-satoshi font-medium text-xs sm:text-sm whitespace-nowrap transition-colors ${
                      activeTab === "copytrading"
                        ? "bg-white text-gray-900 shadow-sm"
                        : "text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                    }`}
                  >
                    <FiUsers className="w-3.5 h-3.5 sm:w-4 sm:h-4 shrink-0" />
                    {t("portfolio.tab.copytrading")}
                  </button>
                  <button
                    onClick={() => setActiveTab("alerts")}
                    className={`flex-1 flex items-center justify-center gap-1 py-2.5 rounded-[5px] font-satoshi font-medium text-xs sm:text-sm whitespace-nowrap transition-colors ${
                      activeTab === "alerts"
                        ? "bg-white text-gray-900 shadow-sm"
                        : "text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                    }`}
                  >
                    <FiTarget className="w-3.5 h-3.5 sm:w-4 sm:h-4 shrink-0" />
                    {t("portfolio.tab.alerts")}
                  </button>
                </div>

                {/* Content */}
                <div key={`${activeTab}-${refreshKey}`} className="animate-tab-enter">
                  {activeTab === "positions" && (
                    <PositionsSection
                      unifiedPositions={unifiedPositions}
                      externalLoading={positionsLoading}
                      externalError={positionsError}
                      onRefetch={refetchPositions}
                    />
                  )}
                  {activeTab === "activity" && (
                    <RecentActivitySection />
                  )}
                  {activeTab === "copytrading" && (
                    <CopyTradingSection />
                  )}
                  {activeTab === "alerts" && (
                    <PriceAlertsSection />
                  )}
                </div>
              </div>

            </div>
          </>
        ) : (
          <div className="py-12">
            <div className="text-center">
              <FiPieChart className="w-16 h-16 text-gray-300 dark:text-[#1A1A1A] mx-auto mb-4" />
              <h2 className="text-xl font-monument font-bold text-gray-900 dark:text-white mb-2">
                {t("portfolio.signInTitle")}
              </h2>
              <p className="text-gray-500 dark:text-gray-400">
                {t("portfolio.signInSubtitle")}
              </p>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

// Main export with Suspense boundary for useSearchParams
export default function PortfolioPage() {
  return (
    <Suspense fallback={
      <div className="w-full pt-6">
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="w-8 h-8 border-2 border-gray-400 dark:border-gray-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    }>
      <PortfolioContent />
    </Suspense>
  );
}
